import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Eye, EyeOff, Loader2, Lock, Trash2, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

const RULES = [
  { id: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "upper", label: "Contains an uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "Contains a lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "Contains a digit (0–9)", test: (p) => /\d/.test(p) },
  { id: "special", label: "Contains a special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PwField({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="pr-10"/>
      <button type="button" tabIndex={-1} onClick={()=>setShow(s=>!s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
        {show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
      </button>
    </div>
  );
}

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const complexity = RULES.map(r => ({ ...r, ok: r.test(next) }));
  const allRulesOk = complexity.every(r => r.ok);
  const matches = next.length > 0 && next === confirm;
  const canSubmit = !!cur && allRulesOk && matches;

  const reset = () => { setCur(""); setNext(""); setConfirm(""); setError(""); };

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next, confirm_password: confirm });
      toast.success("Password updated successfully. Other sessions have been signed out.");
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>{ onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="change-password-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4 text-cyan-700"/> Change password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Current password</Label><PwField value={cur} onChange={setCur} placeholder="Enter current password" testid="input-current-pw"/></div>
          <div><Label>New password</Label><PwField value={next} onChange={setNext} placeholder="Enter new password" testid="input-new-pw"/></div>
          <ul className="grid grid-cols-1 gap-1 text-xs mt-1" data-testid="pw-checklist">
            {complexity.map(r => (
              <li key={r.id} className="flex items-center gap-1.5">
                {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> : <X className="w-3.5 h-3.5 text-slate-300"/>}
                <span className={r.ok ? "text-emerald-700" : "text-slate-500"}>{r.label}</span>
              </li>
            ))}
          </ul>
          <div><Label>Confirm new password</Label><PwField value={confirm} onChange={setConfirm} placeholder="Re-type new password" testid="input-confirm-pw"/>
            {confirm.length > 0 && !matches && <div className="text-xs text-red-600 mt-1" data-testid="pw-mismatch">Passwords do not match</div>}
          </div>
          {error && <div className="text-sm text-red-600" data-testid="change-pw-error">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)} data-testid="btn-cancel-change-pw">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || busy} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50" data-testid="btn-submit-change-pw">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Update password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteAccountDialog({ open, onOpenChange }) {
  const [pw, setPw] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();

  const canSubmit = pw.length > 0 && confirmText === "DELETE";
  const reset = () => { setPw(""); setConfirmText(""); setError(""); };

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/auth/delete-account", { password: pw, confirm_text: confirmText });
      toast.success("Your account has been deleted.");
      setUser(false);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Deletion failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>{ onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="delete-account-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-4 h-4"/> Delete account</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border border-red-200 bg-red-50 rounded-md p-3 text-sm text-red-900" data-testid="delete-warning">
            <div className="font-semibold mb-1">This action is permanent and irreversible.</div>
            You will lose all testing history, tester score, pending payouts, and access to any active projects. Your personal data will be scrubbed from our systems to comply with GDPR / CCPA. Some audit records will be retained.
          </div>
          <div><Label>Re-enter your password</Label><PwField value={pw} onChange={setPw} placeholder="Current password" testid="input-delete-pw"/></div>
          <div>
            <Label>Type <span className="font-mono font-bold text-red-700">DELETE</span> to confirm</Label>
            <Input value={confirmText} onChange={e=>setConfirmText(e.target.value)} placeholder="DELETE" data-testid="input-delete-confirm" className="mt-1"/>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="delete-error">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)} data-testid="btn-cancel-delete">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || busy} className="bg-red-600 hover:bg-red-700 disabled:opacity-50" data-testid="btn-confirm-delete">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Trash2 className="w-4 h-4 mr-1.5"/>Delete my account</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
